import classes from './RichTextEditore.module.css';
import '../../../registerSyncfusion';
import { HtmlEditor, Inject, Link, QuickToolbar, RichTextEditorComponent, Toolbar, } from '@syncfusion/ej2-react-richtexteditor';
import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, } from 'react';
import { useSelector } from 'react-redux';
import { AccessibilityVisuallyHidden } from '@skillz/accessibility';
import loadRichTextEditorLocale from './loadRichTextEditorLocale';

loadRichTextEditorLocale();

const withLinkStyle = (html = '') => {
    if (!html) return html;
    const el = document.createElement('div');
    el.innerHTML = html;
    el.querySelectorAll('a[href]').forEach((a) => a.setAttribute('style', 'text-decoration: underline; color: blue'));
    return el.innerHTML;
};

const RichTextEditore = forwardRef(({ id: idProp, richTextValue, setRichTextValue, value, onChange, otherButtons, moreToolbarItems = [], className, placeholder, onEditorHeightDemandChange, rtl, height = '100%' }, ref) => {

    const generatedId = useId().replace(/:/g, '');
    const id = idProp ?? generatedId;

    const resolvedValue = richTextValue !== undefined ? richTextValue : value;
    const resolvedSetValue = setRichTextValue || onChange;

    const editorRef = useRef(null);
    const inputRef = useRef(null);

    const { texts, direction: directionFromStore, lang } = useSelector((state) => state.translate);
    const direction = rtl === true ? 'rtl' : rtl === false ? 'ltr' : directionFromStore || 'rtl';
    const editorLocale = lang === 'ar' ? 'ar' : lang === 'he' ? 'he' : 'en';

    const insertHTML = (html) => {
        if (editorRef.current) {
            editorRef.current.executeCommand('insertHTML', `${html}`);
            editorRef.current.updateValue();
            resolvedSetValue?.(withLinkStyle(editorRef.current.value));
        }
    };

    useImperativeHandle(ref, () => ({
        insertHTML,
    }));

    const reportEditorOverflow = () => {
        if (!editorRef.current) return;

        const contentEl = editorRef.current.element?.querySelector('.e-rte-content');
        const editorBodyEl = editorRef.current.element?.querySelector('.e-rte-content .e-content');

        if (!contentEl || !editorBodyEl) return;

        const visibleHeight = contentEl.clientHeight;

        const actualContentHeight = Array.from(editorBodyEl.childNodes).reduce((max, node) => {
            if (node.nodeType !== 1) return max;
            const el = node;
            return Math.max(max, el.offsetTop + el.offsetHeight);
        }, 0);

        const heightOverflow = actualContentHeight - visibleHeight;

        onEditorHeightDemandChange?.(heightOverflow);
    };

    const changeRichTextValue = (e) => {
        resolvedSetValue?.(withLinkStyle(e.value));
        reportEditorOverflow();
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === 8) {
            try {
                const selection = window.getSelection();
                const range = selection.getRangeAt(0);
                const selectedElement = range.startContainer.parentElement;

                if (selectedElement?.getAttribute('data-bibliography-id')) {
                    event.preventDefault();
                    selectedElement.remove();
                    resolvedSetValue?.(withLinkStyle(editorRef.current.value));
                }
            } catch (e) {
                console.error('error in delete bibliography with one backspace');
            }
        }
    };

    useEffect(() => {
        const inputElement = editorRef.current?.inputElement;

        if (inputElement) {
            inputElement.setAttribute('dir', direction || 'rtl');
            inputElement.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            inputElement?.removeEventListener('keydown', handleKeyDown);
        };
    }, [direction]);

    const handleFocus = () => {
        const customButton = document.getElementById('custom_tbar');

        if (customButton) {
            customButton.tabIndex = 0;
        }
    };

    const toolbarSettings = {
        items: [
            ...moreToolbarItems,
            'Bold',
            'Italic',
            'Underline',
            'CreateLink',
            'Alignments',
            'OrderedList',
            'UnorderedList',
            // 'BackgroundColor',
            // 'FontColor',
            'Undo',
            'Redo',
        ],
    };

    const placeholderText = placeholder || texts?.writeHere || texts?.writeHereInMessage || '';

    return (
        <div
            className={classes.RichTextInput}
            ref={inputRef}
            dir={direction || 'rtl'}
        >
            <AccessibilityVisuallyHidden>
                <label id={`labelRich-${id}`} htmlFor={`richText-${id}`}>
                    {texts?.MessageContent || placeholderText}
                </label>
            </AccessibilityVisuallyHidden>

            <RichTextEditorComponent
                height={height}
                key={`${id}-${direction}-${editorLocale}`}
                id={`richText-${id}`}
                className={className}
                saveInterval={500}
                aria-labelledby={`labelRich-${id}`}
                placeholder={placeholderText}
                ref={editorRef}
                toolbarSettings={toolbarSettings}
                value={resolvedValue !== undefined ? resolvedValue : ''}
                locale={editorLocale}
                pasteCleanupSettings={{
                    keepFormat: false,
                    removeLineBreaks: true,
                    plainText: false,
                }}
                enableRtl={direction === 'rtl'}
                change={(e) => {
                    if (e.action !== 'tab') {
                        changeRichTextValue(e);
                    }
                }}
                focus={handleFocus}
            >
                <Inject services={[Toolbar, Link, HtmlEditor, QuickToolbar]} />
            </RichTextEditorComponent>

            {otherButtons && otherButtons}
        </div>
    );
}
);

export default RichTextEditore;